import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

const CALENDLY_SCRIPT_SRC = "https://assets.calendly.com/assets/external/widget.js";

let calendlyScriptPromise = null;

function loadCalendlyScript() {
    if (calendlyScriptPromise) return calendlyScriptPromise;
    calendlyScriptPromise = new Promise((resolve, reject) => {
        if (window.Calendly) {
            resolve(window.Calendly);
            return;
        }
        const existing = document.querySelector(`script[src="${CALENDLY_SCRIPT_SRC}"]`);
        const script = existing || document.createElement("script");
        script.addEventListener("load", () => resolve(window.Calendly), { once: true });
        script.addEventListener("error", reject, { once: true });
        if (!existing) {
            script.src = CALENDLY_SCRIPT_SRC;
            script.async = true;
            document.body.appendChild(script);
        }
    });
    return calendlyScriptPromise;
}

function embedUrl(url) {
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}hide_gdpr_banner=1`;
}

/**
 * Inline Calendly scheduler using Calendly's official widget.js embed
 * (instead of a raw iframe), so the booking flow benefits from Calendly's
 * own load optimizations and its postMessage events fire for conversion
 * tracking (booking completion is forwarded to PostHog below).
 * Callers should pair it with a visible "Open the scheduling page" link so
 * booking still works where a host blocks third-party frames.
 */
export default function CalendlyInline({ url, className }) {
    const containerRef = useRef(null);
    const [widgetStarted, setWidgetStarted] = useState(false);

    useEffect(() => {
        let cancelled = false;

        loadCalendlyScript().then((Calendly) => {
            if (cancelled || !Calendly || !containerRef.current) return;
            Calendly.initInlineWidget({
                url: embedUrl(url),
                parentElement: containerRef.current,
            });
            setWidgetStarted(true);
        });

        const onMessage = (e) => {
            if (typeof e.data?.event !== "string" || !e.data.event.startsWith("calendly.")) return;
            if (e.data.event === "calendly.event_scheduled") {
                window.posthog?.capture("discovery_call_booked", { calendar_url: url });
            }
        };
        window.addEventListener("message", onMessage);

        return () => {
            cancelled = true;
            setWidgetStarted(false);
            window.removeEventListener("message", onMessage);
        };
    }, [url]);

    return (
        <div
            className={cn("relative bg-white", className)}
            data-testid="calendly-inline"
        >
            {!widgetStarted && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span className="font-mono text-xs uppercase tracking-widest text-pa-ink-500">
                        Loading calendar…
                    </span>
                </div>
            )}
            <div
                ref={containerRef}
                className="relative w-full h-full"
                style={{ minWidth: 320 }}
            />
        </div>
    );
}
